import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";

export default function PricingSection({ onFree, onPaid, analysisId, currentTier }) {
  const [tiers, setTiers] = useState([]);
  const [monitorInterval, setMonitorInterval] = useState("year");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setTiers(data.tiers)).catch(() => {});
  }, []);

  const handle = (tier, lookupKey) => {
    if (tier.id === "free") {
      onFree ? onFree() : navigate("/");
    } else {
      const t = lookupKey ? { ...tier, lookup_key: lookupKey } : tier;
      onPaid ? onPaid(t) : navigate("/login");
    }
  };

  return (
    <div className="grid gap-px border border-black/10 bg-black/10 lg:grid-cols-3" data-testid="pricing-section">
      {tiers.map((tier) => {
        const featured = tier.id === "report";
        const isMonitor = tier.id === "monitor" && tier.billing_options?.length;
        const selected = isMonitor
          ? tier.billing_options.find((o) => o.interval === monitorInterval) || tier.billing_options[0]
          : null;
        return (
          <div key={tier.id} className={`flex flex-col bg-white p-8 ${featured ? "lg:-my-4 lg:border-2 lg:border-klein lg:shadow-lg" : ""}`} data-testid={`pricing-tier-${tier.id}`}>
            {featured && <span className="mono-label !text-klein">Most popular</span>}
            <h3 className="font-display text-2xl">{tier.name}</h3>

            {isMonitor ? (
              <>
                <div className="mt-4 inline-flex self-start border border-black/15 p-0.5" data-testid="monitor-interval-toggle">
                  {tier.billing_options.map((o) => (
                    <button
                      key={o.interval}
                      data-testid={`monitor-interval-${o.interval}`}
                      onClick={() => setMonitorInterval(o.interval)}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${monitorInterval === o.interval ? "bg-brand-dark text-white" : "text-slate-500 hover:text-ink"}`}
                    >
                      {o.label}{o.savings_pct ? ` · save ${o.savings_pct}%` : ""}
                    </button>
                  ))}
                </div>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-mono-data text-4xl font-semibold" data-testid="monitor-price">
                    ${selected.interval === "year" ? selected.per_month : selected.price}
                  </span>
                  <span className="mb-1 text-sm text-slate-500">/ month</span>
                </div>
                <p className="mt-1 text-xs text-slate-500" data-testid="monitor-price-note">
                  {selected.interval === "year"
                    ? `Billed $${selected.price}/year · ${selected.savings_note}`
                    : "Billed monthly · cancel anytime"}
                </p>
              </>
            ) : (
              <div className="mt-4 flex items-end gap-1">
                <span className="font-mono-data text-4xl font-semibold">${tier.price}</span>
                <span className="mb-1 text-sm text-slate-500">/ {tier.period}</span>
              </div>
            )}

            <ul className="mt-6 flex-1 space-y-3">
              {tier.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check size={16} className="mt-0.5 shrink-0 text-klein" />{f}
                </li>
              ))}
            </ul>
            <Button
              data-testid={`pricing-cta-${tier.id}`}
              onClick={() => handle(tier, isMonitor ? selected.lookup_key : null)}
              disabled={currentTier === tier.id}
              className={`mt-8 h-12 rounded-none ${featured ? "bg-klein text-white hover:bg-brand-dark" : "bg-brand-dark text-white hover:bg-klein"}`}
            >
              {currentTier === tier.id
                ? "Current plan"
                : tier.id === "free"
                ? "Start Free Scan"
                : tier.id === "report"
                ? "Get the Report — $19"
                : selected?.interval === "year"
                ? `Start Monitoring — $${selected.price}/yr`
                : "Start Monitoring — $32.50/mo"}
            </Button>
          </div>
        );
      })}
    </div>
  );
}
